import { supabase } from './supabase';
import type { FoodItem, Recipe } from './store';
import { getWeekRange } from './week-utils';
import { AIUsageService } from './aiUsageService';

// Database types based on our schema
export interface DatabaseFoodItem {
  id: string;
  user_id: string;
  name: string;
  category: string;
  location: 'fridge' | 'pantry' | 'freezer';
  quantity: number;
  unit: string;
  expiry_date: string;
  emoji: string | null;
  created_at: string;
  updated_at: string;
}

export interface DatabaseRecipe {
  id: string;
  user_id?: string | null;
  title: string;
  description: string | null;
  cook_time: number | null;
  prep_time: number | null;
  servings: number | null;
  calories: number | null;
  nutrition: any | null;
  meal_type: string | null;
  cuisine_style: string | null;
  spice_level: 'mild' | 'medium' | 'spicy' | null;
  estimated_cost: number | null;
  difficulty: 'easy' | 'medium' | 'hard';
  ingredients: any; // JSONB array
  instructions: any; // JSONB array
  tags: string[] | null;
  dietary_preferences: string[] | null;
  user_allergies: string[] | null;
  image_url: string | null;
  thumbnail_url: string | null; // 🆕 Optimized thumbnail for fast loading
  created_at: string;
  updated_at: string;
}

export interface DatabaseUserRecipe {
  user_id: string;
  recipe_id: string;
  is_favorite: boolean;
  created_at: string;
}

export interface DatabaseProfile {
  id: string;
  name: string | null;
  email: string | null;
  avatar_url: string | null;
  points: number;
  level: number;
  preferences: any;
  api_provider: string | null;
  is_admin: boolean | null;
  created_at: string;
  updated_at: string;
  // Body Stats & Goals
  age?: number | null;
  gender?: 'male' | 'female' | 'other' | null;
  height?: number | null; // in cm
  weight?: number | null; // in kg
  target_weight?: number | null; // in kg
  goal?: 'lose' | 'maintain' | 'gain' | null;
  activity_level?: 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active' | null;
  daily_calorie_target?: number | null; // calculated based on other stats
  // Custom Nutrition Targets
  custom_calories?: number | null;
  custom_protein?: number | null;
  custom_carbs?: number | null;
  custom_fat?: number | null;
  custom_fiber?: number | null;
  has_custom_targets?: boolean | null;
}

// Meal types
export interface DatabaseMeal {
  id: string;
  user_id: string;
  meal_name: string;
  image_url: string;
  serving_size: string | null;
  confidence: number;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
  sugar: number;
  sodium: number;
  ingredients: string[];
  ai_suggestions: string[];
  source: 'photo_scan' | 'recipe' | null;
  created_at: string;
  updated_at: string;
}

export interface UserMeal {
  id: string;
  mealName: string;
  imageUrl: string;
  servingSize: string;
  confidence: number;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
  sugar: number;
  sodium: number;
  ingredients: string[];
  aiSuggestions: string[];
  source?: 'photo_scan' | 'recipe';
  createdAt: string;
  updatedAt: string;
}

// Meal Generation Preferences types
export interface DatabaseMealGenerationPreferences {
  id: string;
  user_id: string;
  default_source: 'inventory' | 'mixed';
  include_shopping_items: boolean;
  prioritize_expiring: boolean;
  max_cook_time: number;
  preferred_servings: number;
  preferred_difficulty: 'any' | 'easy' | 'medium' | 'hard';
  preferred_meal_type: 'any' | 'breakfast' | 'lunch' | 'dinner' | 'snack' | 'dessert';
  additional_protein: boolean;
  additional_vegetables: boolean;
  preferred_cuisine_style: 'any' | 'italian' | 'asian' | 'mexican' | 'mediterranean' | 'indian' | 'american';
  preferred_spice_level: 'none' | 'mild' | 'medium' | 'spicy';
  healthy_focus: boolean;
  budget_friendly: boolean;
  created_at: string;
  updated_at: string;
}

export interface MealGenerationPreferences {
  id: string;
  defaultSource: 'inventory' | 'mixed';
  includeShoppingItems: boolean;
  prioritizeExpiring: boolean;
  maxCookTime: number;
  preferredServings: number;
  preferredDifficulty: 'any' | 'easy' | 'medium' | 'hard';
  preferredMealType: 'any' | 'breakfast' | 'lunch' | 'dinner' | 'snack' | 'dessert';
  additionalProtein: boolean;
  additionalVegetables: boolean;
  preferredCuisineStyle: 'any' | 'italian' | 'asian' | 'mexican' | 'mediterranean' | 'indian' | 'american';
  preferredSpiceLevel: 'none' | 'mild' | 'medium' | 'spicy';
  healthyFocus: boolean;
  budgetFriendly: boolean;
  createdAt: string;
  updatedAt: string;
}

// Default avatar interface
export interface DefaultAvatar {
  id: number;
  name: string;
  url: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
}

export interface DatabaseShoppingList {
  id: string;
  user_id: string;
  title: string;
  notes: string | null;
  is_completed: boolean;
  created_at: string;
  updated_at: string;
}

export interface DatabaseShoppingListItem {
  id: string;
  shopping_list_id: string;
  name: string;
  quantity: number;
  unit: string | null;
  category: string | null;
  is_purchased: boolean;
  price: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ShoppingList {
  id: string;
  title: string;
  notes: string | null;
  isCompleted: boolean;
  items: ShoppingListItem[];
  createdAt: string;
  updatedAt: string;
}

export interface ShoppingListItem {
  id: string;
  name: string;
  quantity: number;
  unit: string | null;
  category: string | null;
  isPurchased: boolean;
  price: number | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

// Helper functions to transform database types to application types
const transformFoodItem = (dbItem: DatabaseFoodItem): FoodItem => ({
  id: dbItem.id,
  name: dbItem.name,
  category: dbItem.category,
  location: dbItem.location,
  quantity: dbItem.quantity,
  unit: dbItem.unit,
  expiryDate: dbItem.expiry_date,
  emoji: dbItem.emoji || '📦',
});

const transformRecipe = (dbRecipe: DatabaseRecipe, isFavorite: boolean = false): Recipe => ({
  id: dbRecipe.id,
  title: dbRecipe.title,
  description: dbRecipe.description || undefined,
  ingredients: Array.isArray(dbRecipe.ingredients) 
    ? dbRecipe.ingredients.map((ing: any) => ({
        name: ing.name || ing,
        amount: ing.amount || '1 serving',
        category: ing.category || 'other',
        fromInventory: ing.fromInventory || false
      }))
    : (typeof dbRecipe.ingredients === 'string' ? JSON.parse(dbRecipe.ingredients) : []),
  time: dbRecipe.cook_time || 30, // Default to 30 minutes if not set
  prepTime: dbRecipe.prep_time || undefined,
  servings: dbRecipe.servings || undefined,
  calories: dbRecipe.calories || 0,
  nutrition: dbRecipe.nutrition || undefined,
  difficulty: dbRecipe.difficulty,
  mealType: dbRecipe.meal_type || undefined,
  cuisineStyle: dbRecipe.cuisine_style || undefined,
  spiceLevel: dbRecipe.spice_level || undefined,
  estimatedCost: dbRecipe.estimated_cost || undefined,
  tags: dbRecipe.tags || undefined,
  dietary_preferences: dbRecipe.dietary_preferences || undefined,
  user_allergies: dbRecipe.user_allergies || undefined,
  isFavorite,
  imageUrl: dbRecipe.image_url || '',
  thumbnailUrl: dbRecipe.thumbnail_url || undefined, // 🆕 Use thumbnail if available
  createdAt: dbRecipe.created_at, // Map database created_at to Recipe createdAt
});

const transformMeal = (dbMeal: DatabaseMeal): UserMeal => ({
  id: dbMeal.id,
  mealName: dbMeal.meal_name,
  imageUrl: dbMeal.image_url,
  servingSize: dbMeal.serving_size || '',
  confidence: dbMeal.confidence,
  calories: dbMeal.calories,
  protein: dbMeal.protein,
  carbs: dbMeal.carbs,
  fat: dbMeal.fat,
  fiber: dbMeal.fiber,
  sugar: dbMeal.sugar,
  sodium: dbMeal.sodium,
  ingredients: dbMeal.ingredients,
  aiSuggestions: dbMeal.ai_suggestions,
  source: dbMeal.source || undefined,
  createdAt: dbMeal.created_at,
  updatedAt: dbMeal.updated_at,
});

const transformMealGenerationPreferences = (dbPrefs: DatabaseMealGenerationPreferences): MealGenerationPreferences => ({
  id: dbPrefs.id,
  defaultSource: dbPrefs.default_source,
  includeShoppingItems: dbPrefs.include_shopping_items,
  prioritizeExpiring: dbPrefs.prioritize_expiring,
  maxCookTime: dbPrefs.max_cook_time,
  preferredServings: dbPrefs.preferred_servings,
  preferredDifficulty: dbPrefs.preferred_difficulty,
  preferredMealType: dbPrefs.preferred_meal_type,
  additionalProtein: dbPrefs.additional_protein,
  additionalVegetables: dbPrefs.additional_vegetables,
  preferredCuisineStyle: dbPrefs.preferred_cuisine_style,
  preferredSpiceLevel: dbPrefs.preferred_spice_level,
  healthyFocus: dbPrefs.healthy_focus,
  budgetFriendly: dbPrefs.budget_friendly,
  createdAt: dbPrefs.created_at,
  updatedAt: dbPrefs.updated_at,
});

const transformShoppingListItem = (dbItem: DatabaseShoppingListItem): ShoppingListItem => ({
  id: dbItem.id,
  name: dbItem.name,
  quantity: dbItem.quantity,
  unit: dbItem.unit,
  category: dbItem.category,
  isPurchased: dbItem.is_purchased,
  price: dbItem.price,
  notes: dbItem.notes,
  createdAt: dbItem.created_at,
  updatedAt: dbItem.updated_at,
});

// Utility function to add timeout to async operations
const withTimeout = <T>(promise: Promise<T>, timeoutMs: number = 10000): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => 
      setTimeout(() => reject(new Error('Request timeout')), timeoutMs)
    )
  ]);
};

// Helper function to get local date string in YYYY-MM-DD format
// This ensures nutrition tracking uses user's local date, not UTC date
const getLocalDateString = (date?: Date): string => {
  const d = date || new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Database service class
export class DatabaseService {
  private static instance: DatabaseService | null = null;
  private lastUserIdLog: string | null = null;
  private lastUserIdLogTime: number = 0;
  private sessionAuthLogged: boolean = false;
  private sessionStartTime: number = 0;
  private authCache: { userId: string; timestamp: number } | null = null;

  // Constructor - Use getInstance() instead of direct instantiation
  constructor() {}

  // Singleton getInstance method
  public static getInstance(): DatabaseService {
    if (!DatabaseService.instance) {
      DatabaseService.instance = new DatabaseService();
    }
    return DatabaseService.instance;
  }

  // Enhanced authentication caching helper methods
  private shouldLogAuthentication(userId: string, userIdShort: string, now: number): boolean {
    // Check if we have a valid cache entry for this user within 30 seconds
    if (this.authCache && 
        this.authCache.userId === userId && 
        (now - this.authCache.timestamp) < 30000) {
      return false; // Don't log, use cached authentication
    }

    // Check for different user or long session (5 minutes)
    const isDifferentUser = this.lastUserIdLog !== userIdShort;
    const isLongSession = !this.sessionAuthLogged || (now - this.sessionStartTime > 300000);
    return isDifferentUser || isLongSession;
  }

  private updateAuthenticationCache(userId: string, userIdShort: string, now: number): void {
    // Update short-term cache (30 seconds)
    this.authCache = { userId, timestamp: now };
    
    // Update long-term session tracking (5 minutes)
    this.lastUserIdLog = userIdShort;
    this.lastUserIdLogTime = now;
    this.sessionAuthLogged = true;
    this.sessionStartTime = now;
  }

  private async getCurrentUserId(): Promise<string | null> {
    try {
      // In PWA standalone mode, sometimes the initial session check fails
      // Try multiple times with different approaches
      let session = null;
      let sessionError = null;

      // First attempt: Get current session
      const { data: sessionData, error: firstError } = await supabase.auth.getSession();
      session = sessionData.session;
      sessionError = firstError;

      // If no session found and we're in PWA mode, try to refresh the session
      if (!session && !sessionError && (window.navigator as any).standalone) {
        console.log('PWA standalone mode detected, attempting session refresh...');
        
        try {
          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
          if (refreshData.session) {
            session = refreshData.session;
            console.log('Successfully refreshed session in PWA mode');
          } else if (refreshError) {
            console.warn('Session refresh failed:', refreshError);
          }
        } catch (refreshErr) {
          console.warn('Session refresh attempt failed:', refreshErr);
        }
      }

      // If still no session, try one more time with getUser()
      if (!session && !sessionError) {
        try {
          const { data: userData, error: userError } = await supabase.auth.getUser();
          if (userData.user && !userError) {
            // User exists but session might be stale, try to get session again
            const { data: retrySession } = await supabase.auth.getSession();
            session = retrySession.session;
            console.log('Retrieved session after user verification');
          }
        } catch (userErr) {
          console.warn('User verification failed:', userErr);
        }
      }
      
      if (sessionError) {
        console.warn('Session error:', sessionError);
        return null;
      }
      
      if (!session) {
        console.log('No active session found after all attempts');
        return null;
      }
      
      const userId = session.user?.id;
      if (userId) {
        const now = Date.now();
        const userIdShort = userId.substring(0, 8) + '...';
        
        // Enhanced authentication caching to prevent parallel operation spam
        if (this.shouldLogAuthentication(userId, userIdShort, now)) {
          // User authenticated
          this.updateAuthenticationCache(userId, userIdShort, now);
        }
      }
      
      return userId || null;
    } catch (error) {
      console.warn('Error getting current user ID:', error);
      return null;
    }
  }

  // Food Items CRUD
  async getFoodItems(): Promise<FoodItem[]> {
    const userId = await this.getCurrentUserId();
    if (!userId) return [];

    const { data, error } = await supabase
      .from('food_items')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching food items:', error);
      return [];
    }

    return data.map(transformFoodItem);
  }

  async addFoodItem(item: Omit<FoodItem, 'id'>): Promise<FoodItem | null> {
    const userId = await this.getCurrentUserId();
    if (!userId) return null;

    const { data, error } = await supabase
      .from('food_items')
      .insert({
        user_id: userId,
        name: item.name,
        category: item.category,
        location: item.location,
        quantity: item.quantity,
        unit: item.unit,
        expiry_date: item.expiryDate,
        emoji: item.emoji,
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding food item:', error);
      return null;
    }

    return transformFoodItem(data);
  }

  async updateFoodItem(id: string, updates: Partial<FoodItem>): Promise<FoodItem | null> {
    const userId = await this.getCurrentUserId();
    if (!userId) return null;

    const updateData: any = {};
    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.category !== undefined) updateData.category = updates.category;
    if (updates.location !== undefined) updateData.location = updates.location;
    if (updates.quantity !== undefined) updateData.quantity = updates.quantity;
    if (updates.unit !== undefined) updateData.unit = updates.unit;
    if (updates.expiryDate !== undefined) updateData.expiry_date = updates.expiryDate;
    if (updates.emoji !== undefined) updateData.emoji = updates.emoji;

    const { data, error } = await supabase
      .from('food_items')
      .update(updateData)
      .eq('id', id)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) {
      console.error('Error updating food item:', error);
      return null;
    }

    return transformFoodItem(data);
  }

  async deleteFoodItem(id: string): Promise<boolean> {
    const userId = await this.getCurrentUserId();
    if (!userId) return false;

    const { error } = await supabase
      .from('food_items')
      .delete()
      .eq('id', id)
      .eq('user_id', userId);

    if (error) {
      console.error('Error deleting food item:', error);
      return false;
    }

    return true;
  }

  // Get expiring items (items expiring within 7 days)
  async getExpiringItems(): Promise<FoodItem[]> {
    const userId = await this.getCurrentUserId();
    if (!userId) return [];

    const sevenDaysFromNow = new Date();
    sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);

    const { data, error } = await supabase
      .from('food_items')
      .select('*')
      .eq('user_id', userId)
      .lte('expiry_date', sevenDaysFromNow.toISOString().split('T')[0])
      .order('expiry_date', { ascending: true });

    if (error) {
      console.error('Error fetching expiring items:', error);
      return [];
    }

    return data.map(transformFoodItem);
  }

  // Recipes CRUD
  async getRecipes(): Promise<Recipe[]> {
    const userId = await this.getCurrentUserId();
    
    if (!userId) {
      console.log('⚠️ No authenticated user - returning empty recipes list');
      return [];
    }

    // Get only the current user's recipes
    const { data: recipes, error: recipesError } = await supabase
      .from('recipes')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (recipesError) {
      console.error('Error fetching recipes:', recipesError);
      return [];
    }

    // Loaded recipes from database
    if (recipes.length > 0) {
              // Latest recipe loaded
    }

    // Get user favorites
    const { data: favorites, error: favoritesError } = await supabase
      .from('user_recipes')
      .select('recipe_id, is_favorite')
      .eq('user_id', userId);

    if (favoritesError) {
      console.error('Error fetching favorites:', favoritesError);
    }

    const favoriteMap = new Map(
      favorites?.map(fav => [fav.recipe_id, fav.is_favorite]) || []
    );

    return recipes.map(recipe => 
      transformRecipe(recipe, favoriteMap.get(recipe.id) || false)
    );
  }

  async toggleRecipeFavorite(recipeId: string): Promise<boolean> {
    const userId = await this.getCurrentUserId();
    if (!userId) return false;

    // Check if relationship exists
    const { data: existing } = await supabase
      .from('user_recipes')
      .select('is_favorite')
      .eq('user_id', userId)
      .eq('recipe_id', recipeId)
      .maybeSingle();

    if (existing) {
      // Update existing relationship
      const { error } = await supabase
        .from('user_recipes')
        .update({ is_favorite: !existing.is_favorite })
        .eq('user_id', userId)
        .eq('recipe_id', recipeId);

      if (error) {
        console.error('Error updating recipe favorite:', error);
        return false;
      }
    } else {
      // Create new relationship
      const { error } = await supabase
        .from('user_recipes')
        .insert({
          user_id: userId,
          recipe_id: recipeId,
          is_favorite: true,
        });

      if (error) {
        console.error('Error creating recipe favorite:', error);
        return false;
      }
    }

    return true;
  }

  // User Profile CRUD
  async getUserProfile(): Promise<DatabaseProfile | null> {
    const userId = await this.getCurrentUserId();
    
    if (!userId) {
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user profile:', error);
        return null;
      }
      
      // If no profile exists, create one
      if (!data) {
        return await this.createUserProfile(userId);
      }
      
      return data;
    } catch (error) {
      console.error('Error in getUserProfile:', error);
      return null;
    }
  }

  async createUserProfile(userId: string): Promise<DatabaseProfile | null> {
    try {
      // Get user email from auth
      const { data: { user } } = await supabase.auth.getUser();
      const userEmail = user?.email;

      // Get a random default avatar
      const defaultAvatar = await this.getRandomDefaultAvatar();

      const { data, error } = await supabase
        .from('profiles')
        .insert({
          id: userId,
          name: userEmail?.split('@')[0] || 'User',
          email: userEmail,
          avatar_url: defaultAvatar,
          points: 0,
          level: 1,
          preferences: { dietary: [], allergies: [] }
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating user profile:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in createUserProfile:', error);
      return null;
    }
  }

  async updateUserProfile(updates: Partial<DatabaseProfile>): Promise<DatabaseProfile | null> {
    const userId = await this.getCurrentUserId();
    if (!userId) return null;

    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();

    if (error) {
      console.error('Error updating user profile:', error);
      return null;
    }

    return data;
  }

  async updateUserPoints(pointsToAdd: number): Promise<boolean> {
    const userId = await this.getCurrentUserId();
    if (!userId) return false;

    // Get current points
    const { data: profile } = await supabase
      .from('profiles')
      .select('points')
      .eq('id', userId)
      .single();

    if (!profile) return false;

    const newPoints = profile.points + pointsToAdd;
    const newLevel = Math.floor(newPoints / 300) + 1;

    const { error } = await supabase
      .from('profiles')
      .update({ 
        points: newPoints,
        level: newLevel 
      })
      .eq('id', userId);

    if (error) {
      console.error('Error updating user points:', error);
      return false;
    }

    return true;
  }

  // Default Avatars CRUD
  async getDefaultAvatars(): Promise<DefaultAvatar[]> {
    const { data, error } = await supabase
      .from('default_avatars')
      .select('*')
      .eq('is_active', true)
      .order('id', { ascending: true });

    if (error) {
      console.error('Error fetching default avatars:', error);
      return [];
    }

    return data || [];
  }

  async getRandomDefaultAvatar(): Promise<string | null> {
    try {
      // Try to use the PostgreSQL function first
      const { data, error } = await supabase
        .rpc('get_random_default_avatar');

      if (error) {
        console.warn('PostgreSQL function get_random_default_avatar not available, using fallback:', error);
        // Fallback: get a random avatar from the table directly
        const { data: avatars, error: avatarsError } = await supabase
          .from('default_avatars')
          .select('url')
          .eq('is_active', true)
          .limit(1);

        if (avatarsError || !avatars || avatars.length === 0) {
          console.warn('No default avatars found in database, using hardcoded fallback');
          // Generate random avatar from 1-15 as fallback
          const randomNum = Math.floor(Math.random() * 15) + 1;
          return `/img/avatars/avatar-${randomNum}.png`;
        }

        return avatars[0].url;
      }

      return data || `/img/avatars/avatar-${Math.floor(Math.random() * 15) + 1}.png`;
    } catch (error) {
      console.error('Error getting random default avatar, using fallback:', error);
      // Generate random avatar from 1-15 as fallback
      const randomNum = Math.floor(Math.random() * 15) + 1;
      return `/img/avatars/avatar-${randomNum}.png`;
    }
  }

  async updateUserAvatar(avatarUrl: string): Promise<boolean> {
    const userId = await this.getCurrentUserId();
    if (!userId) return false;

    const { error } = await supabase
      .from('profiles')
      .update({ avatar_url: avatarUrl })
      .eq('id', userId);

    if (error) {
      console.error('Error updating user avatar:', error);
      return false;
    }

    return true;
  }

  async updateUserName(name: string): Promise<boolean> {
    const userId = await this.getCurrentUserId();
    if (!userId) return false;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ name })
        .eq('id', userId);

      return !error;
    } catch (error) {
      console.error('Error updating user name:', error);
      return false;
    }
  }

  async updateUserApiProvider(apiProvider: 'openai' | 'openrouter'): Promise<boolean> {
    const userId = await this.getCurrentUserId();
    if (!userId) return false;

    const { error } = await supabase
      .from('profiles')
      .update({ api_provider: apiProvider })
      .eq('id', userId);

    if (error) {
      console.error('Error updating user API provider:', error);
      return false;
    }

    console.log('Successfully updated API provider to:', apiProvider);
    return true;
  }

  async updateUserMeasurementSystem(measurementSystem: 'metric' | 'imperial'): Promise<boolean> {
    const userId = await this.getCurrentUserId();
    if (!userId) return false;

    try {
      // Get current preferences
      const profile = await this.getUserProfile();
      if (!profile) return false;

      // Update measurement system in preferences
      const updatedPreferences = {
        ...profile.preferences,
        measurementSystem: measurementSystem
      };

      const { error } = await supabase
        .from('profiles')
        .update({ 
          preferences: updatedPreferences,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) {
        console.error('Error updating measurement system:', error);
        return false;
      }

      console.log('✅ Measurement system updated successfully:', measurementSystem);
      return true;
    } catch (error) {
      console.error('Error in updateUserMeasurementSystem:', error);
      return false;
    }
  }

  async updateFloatingBarTheme(floatingBarTheme: 'light' | 'dark'): Promise<boolean> {
    const userId = await this.getCurrentUserId();
    if (!userId) return false;

    try {
      // Get current preferences
      const profile = await this.getUserProfile();
      if (!profile) return false;

      // Update floating bar theme in preferences
      const updatedPreferences = {
        ...profile.preferences,
        floatingBarTheme: floatingBarTheme
      };

      const { error } = await supabase
        .from('profiles')
        .update({ 
          preferences: updatedPreferences,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) {
        console.error('Error updating floating bar theme:', error);
        return false;
      }

      console.log('✅ Floating bar theme updated successfully:', floatingBarTheme);
      return true;
    } catch (error) {
      console.error('Error in updateFloatingBarTheme:', error);
      return false;
    }
  }

  async updateUserPreferences(preferences: string[]): Promise<boolean> {
    const userId = await this.getCurrentUserId();
    if (!userId) return false;

    try {
      // First get the current preferences to preserve allergies
      const { data: currentProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('preferences')
        .eq('id', userId)
        .single();

      if (fetchError) {
        console.error('Error fetching current preferences:', fetchError);
        return false;
      }

      // Merge dietary preferences into existing preferences
      const currentPreferences = currentProfile?.preferences || {};
      const updatedPreferences = {
        ...currentPreferences,
        dietary: preferences
      };

      const { error } = await supabase
        .from('profiles')
        .update({ preferences: updatedPreferences })
        .eq('id', userId);

      if (error) {
        console.error('Error updating user preferences:', error);
        return false;
      }

      console.log('Successfully updated user preferences:', preferences);
      return true;
    } catch (error) {
      console.error('Error in updateUserPreferences:', error);
      return false;
    }
  }

  async updateUserAllergies(allergies: string[]): Promise<boolean> {
    const userId = await this.getCurrentUserId();
    if (!userId) return false;

    try {
      // First get the current preferences to preserve dietary preferences
      const { data: currentProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('preferences')
        .eq('id', userId)
        .single();

      if (fetchError) {
        console.error('Error fetching current preferences:', fetchError);
        return false;
      }

      // Merge allergies into existing preferences
      const currentPreferences = currentProfile?.preferences || {};
      const updatedPreferences = {
        ...currentPreferences,
        allergies: allergies
      };

      const { error } = await supabase
        .from('profiles')
        .update({ preferences: updatedPreferences })
        .eq('id', userId);

      if (error) {
        console.error('Error updating user allergies:', error);
        return false;
      }

      console.log('Successfully updated user allergies:', allergies);
      return true;
    } catch (error) {
      console.error('Error in updateUserAllergies:', error);
      return false;
    }
  }

  async migrateCustomAllergiesData(): Promise<boolean> {
    const userId = await this.getCurrentUserId();
    if (!userId) return false;

    try {
      // Get current preferences
      const { data: currentProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('preferences')
        .eq('id', userId)
        .single();

      if (fetchError) {
        console.error('Error fetching current preferences for migration:', fetchError);
        return false;
      }

      const currentPreferences = currentProfile?.preferences || {};
      
      // Check if there's a separate customAllergies field
      if (currentPreferences.customAllergies && Array.isArray(currentPreferences.customAllergies)) {
        const existingAllergies = Array.isArray(currentPreferences.allergies) ? currentPreferences.allergies : [];
        const customAllergies = currentPreferences.customAllergies;
        
        // Merge custom allergies into main allergies array (avoid duplicates)
        const mergedAllergies = [...new Set([...existingAllergies, ...customAllergies])];
        
        // Create updated preferences without the separate customAllergies field
        const { customAllergies: _, ...updatedPreferences } = currentPreferences;
        updatedPreferences.allergies = mergedAllergies;
        
        // Update the database
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ preferences: updatedPreferences })
          .eq('id', userId);

        if (updateError) {
          console.error('Error migrating custom allergies data:', updateError);
          return false;
        }
        
        console.log('Successfully migrated custom allergies data:', {
          oldCustomAllergies: customAllergies,
          oldAllergies: existingAllergies,
          newMergedAllergies: mergedAllergies
        });
      }
      
      return true;
    } catch (error) {
      console.error('Error during custom allergies migration:', error);
      return false;
    }
  }

  // Utility methods
  async isOnline(): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id')
        .limit(1);
      
      return !error;
    } catch {
      return false;
    }
  }

  // User Meals CRUD
  async getUserMeals(): Promise<UserMeal[]> {
    const userId = await this.getCurrentUserId();
    if (!userId) return [];

    const { data, error } = await supabase
      .from('user_meals')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching user meals:', error);
      return [];
    }

    return data.map(transformMeal);
  }

  async addUserMeal(meal: {
    mealName: string;
    imageUrl: string;
    servingSize?: string;
    confidence: number;
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
    sugar: number;
    sodium: number;
    ingredients: string[];
    aiSuggestions: string[];
    source?: 'photo_scan' | 'recipe'; // NEW: Source type to track meal origin
  }): Promise<UserMeal | null> {
    const userId = await this.getCurrentUserId();
    if (!userId) return null;

    const { data, error } = await supabase
      .from('user_meals')
      .insert({
        user_id: userId,
        meal_name: meal.mealName,
        image_url: meal.imageUrl,
        serving_size: meal.servingSize || null,
        confidence: meal.confidence,
        calories: meal.calories,
        protein: meal.protein,
        carbs: meal.carbs,
        fat: meal.fat,
        fiber: meal.fiber,
        sugar: meal.sugar,
        sodium: meal.sodium,
        ingredients: meal.ingredients,
        ai_suggestions: meal.aiSuggestions,
        source: meal.source || 'photo_scan' // Default to photo_scan for backward compatibility
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding user meal:', error);
      return null;
    }

    return transformMeal(data);
  }

  async getUserMealById(mealId: string): Promise<UserMeal | null> {
    const userId = await this.getCurrentUserId();
    if (!userId) return null;

    const { data, error } = await supabase
      .from('user_meals')
      .select('*')
      .eq('id', mealId)
      .eq('user_id', userId)
      .single();

    if (error) {
      console.error('Error fetching user meal:', error);
      return null;
    }

    return transformMeal(data);
  }

  async deleteUserMeal(mealId: string): Promise<boolean> {
    const userId = await this.getCurrentUserId();
    if (!userId) return false;

    try {
      // First, get the meal to extract the image URL and source before deletion
      const { data: meal, error: fetchError } = await supabase
        .from('user_meals')
        .select('meal_name, image_url, source')
        .eq('id', mealId)
        .eq('user_id', userId)
        .single();

      if (fetchError) {
        console.error('❌ Error fetching meal for deletion:', fetchError);
        return false;
      }

      console.log('🔍 Meal deletion details:', {
        mealName: meal?.meal_name,
        imageUrl: meal?.image_url,
        source: meal?.source,
        willDeleteImage: meal?.source === 'photo_scan'
      });

      // Delete the meal record from database
      const { error: deleteError } = await supabase
        .from('user_meals')
        .delete()
        .eq('id', mealId)
        .eq('user_id', userId);

      if (deleteError) {
        console.error('❌ Error deleting user meal:', deleteError);
        return false;
      }

      console.log('✅ Successfully deleted meal from database');

      // Delete the associated image from storage - ONLY for photo_scan meals, NOT for recipe meals
      if (meal?.image_url && meal?.source === 'photo_scan') {
        try {
          console.log('🗑️ Deleting photo-scanned meal image from storage:', meal.image_url);
          
          // Extract the storage path from the image URL
          // Handle both direct storage paths and full URLs
          let storagePath = meal.image_url;
          
          if (meal.image_url.startsWith('http')) {
            // If it's a full URL, extract the storage path
            // URLs are typically: https://...supabase.co/storage/v1/object/sign/images/userId/filename.jpg?token=...
            // Or: https://...supabase.co/storage/v1/object/public/images/userId/filename.jpg
            const url = new URL(meal.image_url);
            const pathParts = url.pathname.split('/');
            
            // Find the bucket name and extract path after it
            const bucketIndex = pathParts.findIndex(part => part === 'images');
            if (bucketIndex > 0 && bucketIndex < pathParts.length - 1) {
              storagePath = pathParts.slice(bucketIndex + 1).join('/');
            } else {
              // If we can't parse the URL, try using the image_url as is
              console.warn('⚠️ Could not parse storage path from URL, using as-is:', meal.image_url);
            }
          }
          
          // Remove the image from storage
          const { error: storageError } = await supabase.storage
            .from('images')
            .remove([storagePath]);

          if (storageError) {
            console.warn('⚠️ Failed to delete image from storage (continuing anyway):', storageError);
            // Don't fail the whole operation if image deletion fails
          } else {
            console.log('✅ Successfully deleted meal image from storage:', storagePath);
          }
        } catch (imageError) {
          console.warn('⚠️ Error during image deletion (continuing anyway):', imageError);
          // Don't fail the whole operation if image deletion fails
        }
      } else if (meal?.image_url && meal?.source === 'recipe') {
        console.log('🔗 Meal uses recipe image - preserving image in storage:', meal.image_url);
      } else {
        console.log('ℹ️ No image URL found for meal, skipping image deletion');
      }

      return true;
    } catch (error) {
      console.error('❌ Error in deleteUserMeal:', error);
      return false;
    }
  }

  async updateUserStats(stats: {
    age?: number;
    gender?: 'male' | 'female' | 'other';
    height?: number;
    weight?: number;
    targetWeight?: number;
    goal?: 'lose' | 'maintain' | 'gain';
    activityLevel?: 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active';
  }): Promise<boolean> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      // Convert camelCase to snake_case for database
      const dbStats: any = {};
      if (stats.age !== undefined) dbStats.age = stats.age;
      if (stats.gender !== undefined) dbStats.gender = stats.gender;
      if (stats.height !== undefined) dbStats.height = stats.height;
      if (stats.weight !== undefined) dbStats.weight = stats.weight;
      if (stats.targetWeight !== undefined) dbStats.target_weight = stats.targetWeight;
      if (stats.goal !== undefined) dbStats.goal = stats.goal;
      if (stats.activityLevel !== undefined) dbStats.activity_level = stats.activityLevel;

      console.log('Updating user stats:', dbStats);

      const { error } = await supabase
        .from('profiles')
        .update(dbStats)
        .eq('id', userId);

      if (error) {
        console.error('Supabase error updating user stats:', error);
        throw new Error(`Failed to update profile: ${error.message}`);
      }

      return true;
    } catch (error) {
      console.error('Error updating user stats:', error);
      throw error; // Re-throw to allow caller to handle
    }
  }

  async updateCustomNutritionTargets(targets: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
  }): Promise<boolean> {
    const userId = await this.getCurrentUserId();
    if (!userId) return false;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          custom_calories: targets.calories,
          custom_protein: targets.protein,
          custom_carbs: targets.carbs,
          custom_fat: targets.fat,
          custom_fiber: targets.fiber,
          has_custom_targets: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) {
        console.error('❌ Error updating custom nutrition targets:', error);
        return false;
      }

      console.log('✅ Successfully updated custom nutrition targets');
      return true;
    } catch (error) {
      console.error('❌ Exception in updateCustomNutritionTargets:', error);
      return false;
    }
  }

  // Meal Generation Preferences CRUD - Using profiles.preferences like the working preferences page
  async getMealGenerationPreferences(): Promise<MealGenerationPreferences | null> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      console.log('❌ User not authenticated, cannot fetch meal generation preferences');
      return null;
    }

    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('preferences')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('❌ Error fetching user profile for meal generation preferences:', error);
        return null;
      }

      const mealGenPrefs = profile?.preferences?.mealGeneration;
      if (!mealGenPrefs) {
        console.log('ℹ️ No meal generation preferences found for user, will use defaults');
        return null;
      }

      console.log('✅ Successfully loaded meal generation preferences from profile');
      
      // Transform from stored format to our expected format
      return {
        id: userId, // Use userId as ID
        defaultSource: mealGenPrefs.defaultSource || 'inventory',
        includeShoppingItems: mealGenPrefs.includeShoppingItems || false,
        prioritizeExpiring: mealGenPrefs.prioritizeExpiring || true,
        maxCookTime: mealGenPrefs.maxCookTime || 30,
        preferredServings: mealGenPrefs.preferredServings || 2,
        preferredDifficulty: mealGenPrefs.preferredDifficulty || 'any',
        preferredMealType: mealGenPrefs.preferredMealType || 'any',
        additionalProtein: mealGenPrefs.additionalProtein || false,
        additionalVegetables: mealGenPrefs.additionalVegetables || false,
        preferredCuisineStyle: mealGenPrefs.preferredCuisineStyle || 'any',
        preferredSpiceLevel: mealGenPrefs.preferredSpiceLevel || 'medium',
        healthyFocus: mealGenPrefs.healthyFocus || false,
        budgetFriendly: mealGenPrefs.budgetFriendly || false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
    } catch (error) {
      console.error('❌ Exception in getMealGenerationPreferences:', error);
      return null;
    }
  }

  async saveMealGenerationPreferences(preferences: {
    defaultSource: 'inventory' | 'mixed';
    includeShoppingItems: boolean;
    prioritizeExpiring: boolean;
    maxCookTime: number;
    preferredServings: number;
    preferredDifficulty: 'any' | 'easy' | 'medium' | 'hard';
    preferredMealType: 'any' | 'breakfast' | 'lunch' | 'dinner' | 'snack' | 'dessert';
    additionalProtein: boolean;
    additionalVegetables: boolean;
    preferredCuisineStyle: 'any' | 'italian' | 'asian' | 'mexican' | 'mediterranean' | 'indian' | 'american';
    preferredSpiceLevel: 'none' | 'mild' | 'medium' | 'spicy';
    healthyFocus: boolean;
    budgetFriendly: boolean;
  }): Promise<boolean> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      console.log('❌ User not authenticated, cannot save meal generation preferences');
      return false;
    }

    console.log('💾 Saving meal generation preferences for user:', userId, preferences);

    try {
      // First get the current preferences to preserve other data (same pattern as working preferences)
      const { data: currentProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('preferences')
        .eq('id', userId)
        .single();

      if (fetchError) {
        console.error('❌ Error fetching current preferences:', fetchError);
        return false;
      }

      // Merge meal generation preferences into existing preferences
      const currentPreferences = currentProfile?.preferences || {};
      const updatedPreferences = {
        ...currentPreferences,
        mealGeneration: preferences // Store under 'mealGeneration' key
      };

      const { error } = await supabase
        .from('profiles')
        .update({ 
          preferences: updatedPreferences,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) {
        console.error('❌ Error saving meal generation preferences:', error);
        return false;
      }

      console.log('✅ Successfully saved meal generation preferences to profile');
      return true;
    } catch (error) {
      console.error('❌ Exception in saveMealGenerationPreferences:', error);
      return false;
    }
  }

  async getCompleteMealGenerationContext(): Promise<{
    preferences: MealGenerationPreferences | null;
    inventory: FoodItem[];
    userProfile: DatabaseProfile | null;
    userPreferences: string[];
    userAllergies: string[];
    shoppingItems: any[]; // TODO: Add shopping list items when implemented
  }> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      return {
        preferences: null,
        inventory: [],
        userProfile: null,
        userPreferences: [],
        userAllergies: [],
        shoppingItems: []
      };
    }

    console.log('🔄 Fetching complete meal generation context for user:', userId);

    // Fetch all data needed for meal generation in parallel
    const [preferences, inventory, userProfile] = await Promise.all([
      this.getMealGenerationPreferences(),
      this.getFoodItems(),
      this.getUserProfile()
    ]);

    // Extract user preferences and allergies from profile
    const userPreferences = userProfile?.preferences?.dietary || [];
    const userAllergies = userProfile?.preferences?.allergies || [];

    console.log('✅ Complete meal generation context loaded:', {
      hasPreferences: !!preferences,
      inventoryCount: inventory.length,
      hasUserProfile: !!userProfile,
      userPreferencesCount: userPreferences.length,
      userAllergiesCount: userAllergies.length
    });

    return {
      preferences,
      inventory,
      userProfile,
      userPreferences,
      userAllergies,
      shoppingItems: [] // TODO: Implement shopping list items
    };
  }

  // AI Recipe Generation from Photo-Detected Ingredients
  async generateRecipeFromPhoto(detectedIngredients: string[], options: any, onProgressUpdate?: (phase: string, elapsed: number) => void): Promise<{
    success: boolean;
    recipe?: any;
    context?: any;
    error?: string;
    requiresUpgrade?: boolean;
    limits?: any;
  }> {
    console.log('📸 [DATABASE] generateRecipeFromPhoto called with detected ingredients:', detectedIngredients);
    console.log('📸 [DATABASE] Options:', JSON.stringify(options, null, 2));
    
    const userId = await this.getCurrentUserId();
    if (!userId) {
      console.error('❌ [DATABASE] User not authenticated');
      throw new Error('User not authenticated');
    }

    // Check usage limits before proceeding
    try {
      const limits = await AIUsageService.checkFeatureLimit(userId, 'recipe_generation');
      if (!limits.can_use) {
        console.warn('🚫 [DATABASE] Photo recipe generation blocked by usage limits');
        return { 
          success: false,
          error: 'Usage limit exceeded', 
          limits,
          requiresUpgrade: true 
        };
      }
    } catch (limitError) {
      console.warn('⚠️ [DATABASE] Usage limit check failed, proceeding anyway:', limitError);
    }

    // Starting photo-based AI recipe generation

    const startTime = Date.now();
    let currentPhase = 'initializing';

    // Progress update helper
    const updateProgress = (newPhase: string) => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      console.log(`🔄 [DATABASE] Photo Recipe Progress: ${currentPhase} → ${newPhase} (${elapsed}s)`);
      currentPhase = newPhase;
      onProgressUpdate?.(newPhase, elapsed);
    };

    updateProgress('validating-ingredients');

    // Validate detected ingredients
    if (!detectedIngredients || detectedIngredients.length === 0) {
      throw new Error('No detected ingredients provided. Please upload a photo with visible ingredients first.');
    }

    if (detectedIngredients.length < 2) {
      throw new Error('Need at least 2 detected ingredients to generate a recipe. Try uploading a photo with more visible ingredients.');
    }

    const requestPayload = {
      userId: userId,
      detectedIngredients: detectedIngredients,
      options: options
    };

    console.log('📤 [DATABASE] Calling photo recipe edge function with payload:', JSON.stringify(requestPayload, null, 2));

    try {
      updateProgress('generating-recipe');
      console.log('🔄 [DATABASE] Invoking supabase.functions.invoke("generate-recipe-from-photo")...');
      
      const { data, error } = await supabase.functions.invoke('generate-recipe-from-photo', {
        body: requestPayload
      });

      updateProgress('validating-ingredients-usage');
      
      console.log('📥 [DATABASE] Photo recipe edge function response received:');
      console.log('  - Error:', error);
      console.log('  - Data:', data);

      if (error) {
        console.error('❌ [DATABASE] Photo recipe edge function error:', error);
        throw new Error(`Photo recipe generation failed: ${error.message}`);
      }

      if (!data) {
        console.error('❌ [DATABASE] No data received from photo recipe edge function');
        throw new Error('No data received from photo recipe generation');
      }

      updateProgress('optimizing-instructions');

      console.log('✅ [DATABASE] Photo recipe generation response received:', {
        success: data.success,
        hasRecipe: !!data.recipe,
        recipeName: data.recipe?.name,
        context: data.context
      });

      if (!data.success) {
        console.error('❌ [DATABASE] Photo recipe generation failed:', data.error);
        throw new Error(data.error || 'Photo recipe generation failed');
      }

      updateProgress('finalizing');

      if (data.recipe) {
        console.log('🍳 [DATABASE] Generated photo recipe details:');
        console.log('  - Name:', data.recipe.name);
        console.log('  - Calories:', data.recipe.calories);
        console.log('  - Cook time:', data.recipe.cookTime);
        console.log('  - Photo ingredients used:', data.recipe.ingredients?.map((ing: any) => `${ing.name} (from photo: ${ing.fromPhoto})`));
        console.log('  - Detected ingredients constraint:', data.context?.constrainedToPhoto);
      }

      // Track successful usage
      try {
        await AIUsageService.incrementUsage(
          userId, 
          'recipe_generation', 
          'recipe_from_photo',
          'openai',
          'gpt-4',
          undefined,
          Date.now() - startTime,
          true
        );
      } catch (trackingError) {
        console.warn('⚠️ [DATABASE] Usage tracking failed (continuing anyway):', trackingError);
      }

      updateProgress('complete');
      return data;
    } catch (error) {
      // Track failed usage
      try {
        await AIUsageService.incrementUsage(
          userId, 
          'recipe_generation', 
          'recipe_from_photo',
          'openai',
          'gpt-4',
          undefined,
          Date.now() - startTime,
          false,
          error instanceof Error ? error.message : 'Unknown error'
        );
      } catch (trackingError) {
        console.warn('⚠️ [DATABASE] Usage tracking failed:', trackingError);
      }
      console.error('❌ [DATABASE] Photo recipe generation error:', error);
      console.error('❌ [DATABASE] Error details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate recipe from photo';
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // AI Recipe Generation
  async generateRecipeWithAI(options: any, onProgressUpdate?: (phase: string, elapsed: number) => void): Promise<{
    success: boolean;
    recipe?: any;
    context?: any;
    error?: string;
    requiresUpgrade?: boolean;
    limits?: any;
  }> {
    console.log('🚀 [DATABASE] generateRecipeWithAI called with options:', JSON.stringify(options, null, 2));
    
    const userId = await this.getCurrentUserId();
    if (!userId) {
      console.error('❌ [DATABASE] User not authenticated');
      throw new Error('User not authenticated');
    }

    // Check usage limits before proceeding
    try {
      const limits = await AIUsageService.checkFeatureLimit(userId, 'recipe_generation');
      if (!limits.can_use) {
        console.warn('🚫 [DATABASE] Recipe generation blocked by usage limits');
        return { 
          success: false,
          error: 'Usage limit exceeded', 
          limits,
          requiresUpgrade: true 
        };
      }
    } catch (limitError) {
      console.warn('⚠️ [DATABASE] Usage limit check failed, proceeding anyway:', limitError);
    }

    // Starting AI recipe generation

    const startTime = Date.now();
    let currentPhase = 'initializing';

    // Progress update helper
    const updateProgress = (newPhase: string) => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      console.log(`🔄 [DATABASE] Progress: ${currentPhase} → ${newPhase} (${elapsed}s)`);
      currentPhase = newPhase;
      onProgressUpdate?.(newPhase, elapsed);
    };

    // Start with analyzing preferences
    updateProgress('analyzing-preferences');

    // Get user context for logging
    try {
      const [userProfile, inventory] = await Promise.all([
        this.getUserProfile(),
        this.getFoodItems()
      ]);
      
      updateProgress('checking-inventory');
      
      console.log('📊 [DATABASE] User context loaded:');
      console.log('  - User profile:', {
        name: userProfile?.name,
        apiProvider: userProfile?.api_provider,
        dailyCalorieTarget: userProfile?.daily_calorie_target,
        dietaryPreferences: userProfile?.preferences?.dietary,
        allergies: userProfile?.preferences?.allergies
      });
      console.log('  - Inventory count:', inventory.length);
      console.log('  - Inventory items:', inventory.map(item => `${item.name} (${item.quantity} ${item.unit})`));
    } catch (contextError) {
      console.error('❌ [DATABASE] Failed to load user context for logging:', contextError);
    }

    const requestPayload = {
      userId: userId,
      options: options
    };

    console.log('📤 [DATABASE] Calling edge function with payload:', JSON.stringify(requestPayload, null, 2));

    try {
      updateProgress('generating-recipe');
      
      // Route to generate-recipe function which handles both inventory-only and mixed modes
      const functionName = 'generate-recipe';
      console.log(`🔄 [DATABASE] Invoking supabase.functions.invoke("${functionName}")...`);
      console.log(`🛒 [DATABASE] Mixed mode: ${options.includeShoppingItems ? 'DISCOVERY (zero inventory + pure AI creativity)' : 'INVENTORY-ONLY (uses all inventory items)'}`);
      
      const { data, error } = await supabase.functions.invoke(functionName, {
        body: requestPayload
      });

      updateProgress('calculating-nutrition');
      
      console.log('📥 [DATABASE] Edge function response received:');
      console.log('  - Error:', error);
      console.log('  - Data:', data);

      if (error) {
        console.error('❌ [DATABASE] Edge function error:', error);
        throw new Error(`Recipe generation failed: ${error.message}`);
      }

      if (!data) {
        console.error('❌ [DATABASE] No data received from edge function');
        throw new Error('No data received from recipe generation');
      }

      updateProgress('optimizing-instructions');

      console.log('✅ [DATABASE] Recipe generation response received:', {
        success: data.success,
        hasRecipe: !!data.recipe,
        recipeName: data.recipe?.name,
        context: data.context
      });

      if (!data.success) {
        console.error('❌ [DATABASE] Recipe generation failed:', data.error);
        throw new Error(data.error || 'Recipe generation failed');
      }

      updateProgress('finalizing');

      if (data.recipe) {
        console.log('🍳 [DATABASE] Generated recipe details:');
        console.log('  - Name:', data.recipe.name);
        console.log('  - Calories:', data.recipe.calories);
        console.log('  - Cook time:', data.recipe.cookTime);
        console.log('  - Ingredients used:', data.recipe.ingredients?.map((ing: any) => `${ing.name} (from inventory: ${ing.fromInventory})`));
        console.log('  - Cuisine style:', data.recipe.cuisineStyle);
        console.log('  - Spice level:', data.recipe.spiceLevel);
      }

      // Track successful usage
      try {
        await AIUsageService.incrementUsage(
          userId, 
          'recipe_generation', 
          options.includeShoppingItems ? 'recipe_with_shopping' : 'recipe_from_inventory',
          'openai',
          'gpt-4',
          undefined,
          Date.now() - startTime,
          true
        );
      } catch (trackingError) {
        console.warn('⚠️ [DATABASE] Usage tracking failed (continuing anyway):', trackingError);
      }

      updateProgress('complete');
      return data;
    } catch (error) {
      // Track failed usage
      try {
        await AIUsageService.incrementUsage(
          userId, 
          'recipe_generation', 
          options.includeShoppingItems ? 'recipe_with_shopping' : 'recipe_from_inventory',
          'openai',
          'gpt-4',
          undefined,
          Date.now() - startTime,
          false,
          error instanceof Error ? error.message : 'Unknown error'
        );
      } catch (trackingError) {
        console.warn('⚠️ [DATABASE] Usage tracking failed:', trackingError);
      }
      console.error('❌ [DATABASE] Recipe generation error:', error);
      console.error('❌ [DATABASE] Error details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate recipe';
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // NEW: Recipe Image Generation (Pure Addition - No existing code modified)
  async generateRecipeImage(recipeData: any): Promise<{
    success: boolean;
    imageUrl?: string;
    error?: string;
  }> {
    console.log('🎨 [DATABASE] generateRecipeImage called for recipe:', recipeData?.name);
    
    const userId = await this.getCurrentUserId();
    if (!userId) {
      console.error('❌ [DATABASE] User not authenticated for image generation');
      return { success: false, error: 'User not authenticated' };
    }

    // Starting image generation

    // Validate recipe data
    if (!recipeData || !recipeData.name) {
      console.error('❌ [DATABASE] Invalid recipe data for image generation');
      return { success: false, error: 'Recipe data with name is required' };
    }

    const requestPayload = {
      recipeData: {
        name: recipeData.name,
        description: recipeData.description,
        ingredients: recipeData.ingredients,
        cuisineStyle: recipeData.cuisineStyle,
        mealType: recipeData.mealType
      },
      userId: userId
    };

    console.log('📤 [DATABASE] Calling image generation edge function with payload:', JSON.stringify(requestPayload, null, 2));

    try {
      console.log('🔄 [DATABASE] Invoking supabase.functions.invoke("generate-recipe-image")...');
      
      const { data, error } = await supabase.functions.invoke('generate-recipe-image', {
        body: requestPayload
      });

      console.log('📥 [DATABASE] Image generation edge function response received:');
      console.log('  - Error:', error);
      console.log('  - Data:', data);

      if (error) {
        console.error('❌ [DATABASE] Image generation edge function error:', error);
        return { success: false, error: `Image generation failed: ${error.message}` };
      }

      if (!data) {
        console.error('❌ [DATABASE] No data received from image generation edge function');
        return { success: false, error: 'No data received from image generation' };
      }

      console.log('✅ [DATABASE] Image generation response received:', {
        success: data.success,
        hasImageUrl: !!data.imageUrl,
        model: data.model,
        recipeName: data.recipeName
      });

      if (!data.success) {
        console.error('❌ [DATABASE] Image generation failed:', data.error);
        return { success: false, error: data.error || 'Image generation failed' };
      }

      if (data.imageUrl) {
        console.log('🎨 [DATABASE] Generated image URL received:', {
          urlLength: data.imageUrl.length,
          urlPrefix: data.imageUrl.substring(0, 50) + '...',
          model: data.model
        });
      }

      return {
        success: true,
        imageUrl: data.imageUrl
      };

    } catch (error) {
      console.error('❌ [DATABASE] Image generation error:', error);
      console.error('❌ [DATABASE] Error details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate recipe image';
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // Daily Nutrition Tracking Methods

  /**
   * Add a nutrition entry (meal or recipe) and update daily totals
   */
  async addNutritionEntry(entry: {
    entryType: 'meal' | 'recipe';
    entryId: string;
    entryName: string;
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber?: number;
    sugar?: number;
    sodium?: number;
    servings?: number;
    servingSize?: string;
    trackingDate?: string;
  }): Promise<boolean> {
    const userId = await this.getCurrentUserId();
    if (!userId) return false;

    try {
      // Use timezone-aware local date instead of UTC date
      const trackingDate = entry.trackingDate || getLocalDateString();
      
      const { data, error } = await supabase.rpc('add_nutrition_entry', {
        p_user_id: userId,
        p_tracking_date: trackingDate,
        p_entry_type: entry.entryType,
        p_entry_id: entry.entryId,
        p_entry_name: entry.entryName,
        p_calories: entry.calories,
        p_protein: entry.protein,
        p_carbs: entry.carbs,
        p_fat: entry.fat,
        p_fiber: entry.fiber || 0,
        p_sugar: entry.sugar || 0,
        p_sodium: entry.sodium || 0,
        p_servings: entry.servings || 1.0,
        p_serving_size: entry.servingSize || null
      });

      if (error) {
        console.error('❌ Error adding nutrition entry:', error);
        return false;
      }

      console.log('✅ Successfully added nutrition entry and updated daily totals');
      return data === true;
    } catch (error) {
      console.error('❌ Exception in addNutritionEntry:', error);
      return false;
    }
  }

  /**
   * Get daily nutrition summary for a specific date
   */
  async getDailyNutritionSummary(date?: string): Promise<DailyNutritionSummary | null> {
    const userId = await this.getCurrentUserId();
    if (!userId) return null;

    try {
      // Use timezone-aware local date instead of UTC date
      const trackingDate = date || getLocalDateString();
      
      const { data, error } = await supabase.rpc('get_daily_nutrition_summary', {
        p_user_id: userId,
        p_tracking_date: trackingDate
      });

      if (error) {
        console.error('❌ Error getting daily nutrition summary:', error);
        return null;
      }

      if (!data || data.length === 0) {
        // Return empty summary if no data found
        return {
          totalCalories: 0,
          totalProtein: 0,
          totalCarbs: 0,
          totalFat: 0,
          totalFiber: 0,
          totalSugar: 0,
          totalSodium: 0,
          mealsCount: 0,
          recipesCount: 0,
          entriesCount: 0
        };
      }

      const summary = data[0];
      return {
        totalCalories: summary.total_calories || 0,
        totalProtein: parseFloat(summary.total_protein || '0'),
        totalCarbs: parseFloat(summary.total_carbs || '0'),
        totalFat: parseFloat(summary.total_fat || '0'),
        totalFiber: parseFloat(summary.total_fiber || '0'),
        totalSugar: parseFloat(summary.total_sugar || '0'),
        totalSodium: parseFloat(summary.total_sodium || '0'),
        mealsCount: summary.meals_count || 0,
        recipesCount: summary.recipes_count || 0,
        entriesCount: parseInt(summary.entries_count || '0')
      };
    } catch (error) {
      console.error('❌ Exception in getDailyNutritionSummary:', error);
      return null;
    }
  }

  /**
   * Get nutrition entries for a specific date
   */
  async getDailyNutritionEntries(date?: string): Promise<NutritionEntry[]> {
    const userId = await this.getCurrentUserId();
    if (!userId) return [];

    try {
      // Use timezone-aware local date instead of UTC date
      const trackingDate = date || getLocalDateString();
      
      const { data, error } = await supabase
        .from('daily_nutrition_entries')
        .select('*')
        .eq('user_id', userId)
        .eq('tracking_date', trackingDate)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error getting daily nutrition entries:', error);
        return [];
      }

      return data.map((entry: DatabaseDailyNutritionEntry) => ({
        id: entry.id,
        trackingDate: entry.tracking_date,
        entryType: entry.entry_type,
        entryId: entry.entry_id,
        entryName: entry.entry_name,
        calories: entry.calories,
        protein: parseFloat(entry.protein.toString()),
        carbs: parseFloat(entry.carbs.toString()),
        fat: parseFloat(entry.fat.toString()),
        fiber: parseFloat(entry.fiber.toString()),
        sugar: parseFloat(entry.sugar.toString()),
        sodium: parseFloat(entry.sodium.toString()),
        servings: parseFloat(entry.servings.toString()),
        servingSize: entry.serving_size || '',
        createdAt: entry.created_at,
        updatedAt: entry.updated_at
      }));
    } catch (error) {
      console.error('❌ Exception in getDailyNutritionEntries:', error);
      return [];
    }
  }

  /**
   * Remove a nutrition entry and update daily totals
   */
  async removeNutritionEntry(entryId: string): Promise<boolean> {
    const userId = await this.getCurrentUserId();
    if (!userId) return false;

    try {
      // First get the entry to subtract from daily totals
      const { data: entry, error: fetchError } = await supabase
        .from('daily_nutrition_entries')
        .select('*')
        .eq('id', entryId)
        .eq('user_id', userId)
        .single();

      if (fetchError || !entry) {
        console.error('❌ Error fetching nutrition entry for removal:', fetchError);
        return false;
      }

      // Delete the entry
      const { error: deleteError } = await supabase
        .from('daily_nutrition_entries')
        .delete()
        .eq('id', entryId)
        .eq('user_id', userId);

      if (deleteError) {
        console.error('❌ Error deleting nutrition entry:', deleteError);
        return false;
      }

      // Get current daily totals first
      const { data: currentTotals, error: fetchTotalsError } = await supabase
        .from('daily_nutrition_tracking')
        .select('*')
        .eq('user_id', userId)
        .eq('tracking_date', entry.tracking_date)
        .single();

      if (fetchTotalsError || !currentTotals) {
        console.error('❌ Error fetching daily totals for update:', fetchTotalsError);
        return false;
      }

      // Update daily totals by subtracting the removed entry
      const { error: updateError } = await supabase
        .from('daily_nutrition_tracking')
        .update({
          total_calories: Math.max(0, currentTotals.total_calories - entry.calories),
          total_protein: Math.max(0, parseFloat(currentTotals.total_protein) - parseFloat(entry.protein)),
          total_carbs: Math.max(0, parseFloat(currentTotals.total_carbs) - parseFloat(entry.carbs)),
          total_fat: Math.max(0, parseFloat(currentTotals.total_fat) - parseFloat(entry.fat)),
          total_fiber: Math.max(0, parseFloat(currentTotals.total_fiber) - parseFloat(entry.fiber)),
          total_sugar: Math.max(0, parseFloat(currentTotals.total_sugar) - parseFloat(entry.sugar)),
          total_sodium: Math.max(0, parseFloat(currentTotals.total_sodium) - parseFloat(entry.sodium)),
          meals_count: entry.entry_type === 'meal' ? 
            Math.max(0, currentTotals.meals_count - 1) : currentTotals.meals_count,
          recipes_count: entry.entry_type === 'recipe' ? 
            Math.max(0, currentTotals.recipes_count - 1) : currentTotals.recipes_count,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('tracking_date', entry.tracking_date);

      if (updateError) {
        console.error('❌ Error updating daily totals after removal:', updateError);
        return false;
      }

      console.log('✅ Successfully removed nutrition entry and updated daily totals');
      return true;
    } catch (error) {
      console.error('❌ Exception in removeNutritionEntry:', error);
      return false;
    }
  }

  /**
   * Remove nutrition entry by meal ID and update daily totals
   */
  async removeNutritionEntryByMealId(mealId: string): Promise<boolean> {
    const userId = await this.getCurrentUserId();
    if (!userId) return false;

    try {
      // First get the entry to subtract from daily totals
      const { data: entry, error: fetchError } = await supabase
        .from('daily_nutrition_entries')
        .select('*')
        .eq('entry_id', mealId)
        .eq('entry_type', 'meal')
        .eq('user_id', userId)
        .single();

      if (fetchError || !entry) {
        console.error('❌ Error fetching nutrition entry for meal removal:', fetchError);
        return false;
      }

      // Delete the entry
      const { error: deleteError } = await supabase
        .from('daily_nutrition_entries')
        .delete()
        .eq('entry_id', mealId)
        .eq('entry_type', 'meal')
        .eq('user_id', userId);

      if (deleteError) {
        console.error('❌ Error deleting nutrition entry by meal ID:', deleteError);
        return false;
      }

      // Get current daily totals first
      const { data: currentTotals, error: fetchTotalsError } = await supabase
        .from('daily_nutrition_tracking')
        .select('*')
        .eq('user_id', userId)
        .eq('tracking_date', entry.tracking_date)
        .single();

      if (fetchTotalsError || !currentTotals) {
        console.error('❌ Error fetching daily totals for update after meal removal:', fetchTotalsError);
        return false;
      }

      // Update daily totals by subtracting the removed entry
      const { error: updateError } = await supabase
        .from('daily_nutrition_tracking')
        .update({
          total_calories: Math.max(0, currentTotals.total_calories - entry.calories),
          total_protein: Math.max(0, parseFloat(currentTotals.total_protein) - parseFloat(entry.protein)),
          total_carbs: Math.max(0, parseFloat(currentTotals.total_carbs) - parseFloat(entry.carbs)),
          total_fat: Math.max(0, parseFloat(currentTotals.total_fat) - parseFloat(entry.fat)),
          total_fiber: Math.max(0, parseFloat(currentTotals.total_fiber) - parseFloat(entry.fiber)),
          total_sugar: Math.max(0, parseFloat(currentTotals.total_sugar) - parseFloat(entry.sugar)),
          total_sodium: Math.max(0, parseFloat(currentTotals.total_sodium) - parseFloat(entry.sodium)),
          meals_count: Math.max(0, currentTotals.meals_count - 1),
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('tracking_date', entry.tracking_date);

      if (updateError) {
        console.error('❌ Error updating daily totals after meal removal:', updateError);
        return false;
      }

      console.log('✅ Successfully removed meal nutrition entry and updated daily totals');
      return true;
    } catch (error) {
      console.error('❌ Exception in removeNutritionEntryByMealId:', error);
      return false;
    }
  }

  /**
   * Clean up orphaned nutrition entries (entries that reference deleted meals)
   * This fixes historical data where meals were deleted but nutrition entries remained
   */
  // Meal Planning Functions
  async getMealPlan(startDate: string, endDate: string): Promise<MealPlan | null> {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        console.warn('❌ [DatabaseService] No authenticated user found for meal plan retrieval');
        return null;
      }

      const { data, error } = await supabase
        .from('meal_plans')
        .select('*')
        .eq('user_id', userId)
        .eq('start_date', startDate)
        .eq('end_date', endDate);

      if (error) {
        console.error('❌ [DatabaseService] Error fetching meal plan:', error);
        return null;
      }

      // If no meal plan found, return empty structure
      if (!data || data.length === 0) {
        return {
          id: '',
          startDate,
          endDate,
          meals: {}
        };
      }

      const mealPlan = data[0];
      return {
        id: mealPlan.id,
        startDate: mealPlan.start_date,
        endDate: mealPlan.end_date,
        meals: mealPlan.meals || {}
      };
    } catch (error) {
      console.error('💥 [DatabaseService] Exception in getMealPlan:', error);
      return null;
    }
  }

  async saveMealPlan(mealPlan: {
    startDate: string;
    endDate: string;
    meals: Record<string, any>;
  }): Promise<boolean> {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        console.warn('❌ [DatabaseService] No authenticated user found for meal plan saving');
        return false;
      }

      // Check if meal plan already exists
      const { data: existing, error: fetchError } = await supabase
        .from('meal_plans')
        .select('id')
        .eq('user_id', userId)
        .eq('start_date', mealPlan.startDate)
        .eq('end_date', mealPlan.endDate);

      if (fetchError) {
        console.error('❌ [DatabaseService] Error fetching existing meal plan:', fetchError);
        return false;
      }

      if (existing && existing.length > 0) {
        // Update existing meal plan
        const { error } = await supabase
          .from('meal_plans')
          .update({
            meals: mealPlan.meals,
            updated_at: new Date().toISOString()
          })
          .eq('id', existing[0].id);

        if (error) {
          console.error('❌ [DatabaseService] Error updating meal plan:', error);
          return false;
        }
      } else {
        // Create new meal plan
        const { error } = await supabase
          .from('meal_plans')
          .insert({
            user_id: userId,
            title: `Meal Plan ${mealPlan.startDate} to ${mealPlan.endDate}`,
            description: 'Generated meal plan',
            start_date: mealPlan.startDate,
            end_date: mealPlan.endDate,
            meals: mealPlan.meals
          });

        if (error) {
          console.error('❌ [DatabaseService] Error creating meal plan:', error);
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error('💥 [DatabaseService] Exception in saveMealPlan:', error);
      return false;
    }
  }

  async addMealToWeek(date: string, mealType: 'breakfast' | 'lunch' | 'dinner', recipe: Recipe): Promise<boolean> {
    try {
      // Get current week's meal plan using centralized week calculation
      const weekRange = getWeekRange(new Date(date));
      const mealPlan = await this.getMealPlan(weekRange.startString, weekRange.endString);
      
      if (!mealPlan) {
        console.error('❌ [DatabaseService] No meal plan found for the week');
        return false;
      }

      // Add recipe to specific date and meal type
      const dateKey = date;
      if (!mealPlan.meals[dateKey]) {
        mealPlan.meals[dateKey] = {};
      }

      // Create meal entry
      const mealEntry = {
        recipeId: recipe.id,
        recipeName: recipe.title,
        servings: 1,
        calories: recipe.calories,
        cookTime: recipe.time
      };

      mealPlan.meals[dateKey][mealType] = mealEntry;

      // Save updated meal plan
      const success = await this.saveMealPlan({
        startDate: mealPlan.startDate,
        endDate: mealPlan.endDate,
        meals: mealPlan.meals
      });

      if (!success) {
        console.error('❌ [DatabaseService] Failed to save updated meal plan');
        return false;
      }

      return true;
    } catch (error) {
      console.error('💥 [DatabaseService] Exception in addMealToWeek:', error);
      return false;
    }
  }

  async removeMealFromWeek(date: string, mealType: 'breakfast' | 'lunch' | 'dinner'): Promise<boolean> {
    try {
      // Get current week's meal plan using centralized week calculation
      const weekRange = getWeekRange(new Date(date));
      
      const mealPlan = await this.getMealPlan(weekRange.startString, weekRange.endString);
      if (!mealPlan) return false;

      // Remove meal from specific date and meal type
      const dateKey = date;
      if (mealPlan.meals[dateKey] && mealPlan.meals[dateKey][mealType]) {
        delete mealPlan.meals[dateKey][mealType];
        
        // Clean up empty date objects
        if (Object.keys(mealPlan.meals[dateKey]).length === 0) {
          delete mealPlan.meals[dateKey];
        }
      }

      // Save updated meal plan
      return await this.saveMealPlan({
        startDate: mealPlan.startDate,
        endDate: mealPlan.endDate,
        meals: mealPlan.meals
      });
    } catch (error) {
      console.error('💥 [DatabaseService] Exception in removeMealFromWeek:', error);
      return false;
    }
  }

  private getWeekStart(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    
    // Create a new date object to avoid mutation
    const weekStart = new Date(d);
    weekStart.setDate(diff);
    
    return weekStart;
  }

  private getWeekEnd(date: Date): Date {
    const weekStart = this.getWeekStart(date);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    return weekEnd;
  }

  async cleanupOrphanedNutritionEntries(): Promise<{ cleaned: number; recalculated: string[] }> {
    const userId = await this.getCurrentUserId();
    if (!userId) return { cleaned: 0, recalculated: [] };

    try {
      console.log('🧹 Starting cleanup of orphaned nutrition entries...');

      // Get all nutrition entries for meals
      const { data: nutritionEntries, error: entriesError } = await supabase
        .from('daily_nutrition_entries')
        .select('*')
        .eq('user_id', userId)
        .eq('entry_type', 'meal');

      if (entriesError) {
        console.error('❌ Error fetching nutrition entries:', entriesError);
        return { cleaned: 0, recalculated: [] };
      }

      if (!nutritionEntries || nutritionEntries.length === 0) {
        console.log('ℹ️ No meal nutrition entries found');
        return { cleaned: 0, recalculated: [] };
      }

      // Get all existing meal IDs
      const { data: existingMeals, error: mealsError } = await supabase
        .from('user_meals')
        .select('id')
        .eq('user_id', userId);

      if (mealsError) {
        console.error('❌ Error fetching existing meals:', mealsError);
        return { cleaned: 0, recalculated: [] };
      }

      const existingMealIds = new Set(existingMeals?.map(meal => meal.id) || []);
      
      // Find orphaned entries (nutrition entries for meals that no longer exist)
      const orphanedEntries = nutritionEntries.filter(entry => !existingMealIds.has(entry.entry_id));
      
      if (orphanedEntries.length === 0) {
        console.log('✅ No orphaned nutrition entries found');
        return { cleaned: 0, recalculated: [] };
      }

      console.log(`🗑️ Found ${orphanedEntries.length} orphaned nutrition entries to clean up`);

      // Group orphaned entries by date for efficient recalculation
      const entriesByDate = new Map<string, typeof orphanedEntries>();
      orphanedEntries.forEach(entry => {
        const date = entry.tracking_date;
        if (!entriesByDate.has(date)) {
          entriesByDate.set(date, []);
        }
        entriesByDate.get(date)!.push(entry);
      });

      // Delete orphaned entries
      const orphanedIds = orphanedEntries.map(entry => entry.id);
      const { error: deleteError } = await supabase
        .from('daily_nutrition_entries')
        .delete()
        .in('id', orphanedIds);

      if (deleteError) {
        console.error('❌ Error deleting orphaned entries:', deleteError);
        return { cleaned: 0, recalculated: [] };
      }

      // Recalculate daily totals for affected dates
      const recalculatedDates: string[] = [];
      
      for (const [date, entries] of entriesByDate) {
        try {
          // Get current daily totals
          const { data: currentTotals, error: fetchError } = await supabase
            .from('daily_nutrition_tracking')
            .select('*')
            .eq('user_id', userId)
            .eq('tracking_date', date)
            .single();

          if (fetchError || !currentTotals) {
            console.warn(`⚠️ No daily totals found for ${date}, skipping recalculation`);
            continue;
          }

          // Calculate totals to subtract
          const toSubtract = entries.reduce((acc, entry) => ({
            calories: acc.calories + entry.calories,
            protein: acc.protein + parseFloat(entry.protein),
            carbs: acc.carbs + parseFloat(entry.carbs),
            fat: acc.fat + parseFloat(entry.fat),
            fiber: acc.fiber + parseFloat(entry.fiber),
            sugar: acc.sugar + parseFloat(entry.sugar),
            sodium: acc.sodium + parseFloat(entry.sodium),
            mealCount: acc.mealCount + 1
          }), { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0, sodium: 0, mealCount: 0 });

          // Update daily totals
          const { error: updateError } = await supabase
            .from('daily_nutrition_tracking')
            .update({
              total_calories: Math.max(0, currentTotals.total_calories - toSubtract.calories),
              total_protein: Math.max(0, parseFloat(currentTotals.total_protein) - toSubtract.protein),
              total_carbs: Math.max(0, parseFloat(currentTotals.total_carbs) - toSubtract.carbs),
              total_fat: Math.max(0, parseFloat(currentTotals.total_fat) - toSubtract.fat),
              total_fiber: Math.max(0, parseFloat(currentTotals.total_fiber) - toSubtract.fiber),
              total_sugar: Math.max(0, parseFloat(currentTotals.total_sugar) - toSubtract.sugar),
              total_sodium: Math.max(0, parseFloat(currentTotals.total_sodium) - toSubtract.sodium),
              meals_count: Math.max(0, currentTotals.meals_count - toSubtract.mealCount),
              updated_at: new Date().toISOString()
            })
            .eq('user_id', userId)
            .eq('tracking_date', date);

          if (updateError) {
            console.error(`❌ Error updating daily totals for ${date}:`, updateError);
          } else {
            recalculatedDates.push(date);
            console.log(`✅ Recalculated daily totals for ${date} (removed ${entries.length} orphaned entries)`);
          }
        } catch (error) {
          console.error(`❌ Error processing date ${date}:`, error);
        }
      }

      console.log(`✅ Cleanup complete: ${orphanedEntries.length} orphaned entries removed, ${recalculatedDates.length} dates recalculated`);
      return { cleaned: orphanedEntries.length, recalculated: recalculatedDates };

    } catch (error) {
      console.error('❌ Exception in cleanupOrphanedNutritionEntries:', error);
      return { cleaned: 0, recalculated: [] };
    }
  }

  // 🔧 TEMPORARY DEBUG METHOD - Will remove after testing
  async debugListAllMealPlans(): Promise<any[]> {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        console.warn('❌ [DatabaseService] No authenticated user for debug query');
        return [];
      }

      const { data, error } = await supabase
        .from('meal_plans')
        .select('*')
        .eq('user_id', userId)
        .order('start_date', { ascending: false });

      if (error) {
        console.error('❌ [DatabaseService] Error in debug query:', error);
        return [];
      }

      // Only log summary instead of detailed breakdown
      if (data && data.length > 0) {
        console.log(`📋 [DatabaseService] Found ${data.length} meal plans`);
      } else {
        console.log('📋 [DatabaseService] No meal plans found');
      }

      return data || [];
    } catch (error) {
      console.error('💥 [DatabaseService] Exception in debugListAllMealPlans:', error);
      return [];
    }
  }

  // 🔧 TEMPORARY FIX METHOD - Fix existing meal plans with incorrect week ranges
  async fixExistingMealPlanRanges(): Promise<boolean> {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        console.warn('❌ [DatabaseService] No authenticated user for meal plan fix');
        return false;
      }

      // Get all existing meal plans
      const { data: existingPlans, error: fetchError } = await supabase
        .from('meal_plans')
        .select('*')
        .eq('user_id', userId);

      if (fetchError) {
        console.error('❌ [DatabaseService] Error fetching meal plans for fix:', fetchError);
        return false;
      }

      if (!existingPlans || existingPlans.length === 0) {
        return true;
      }

      let fixedCount = 0;

      // Fix each meal plan
      for (const plan of existingPlans) {
        // For each meal plan, find any meal date and recalculate the correct week
        const mealDates = Object.keys(plan.meals || {});
        if (mealDates.length === 0) {
          continue;
        }

        // Use the first meal date to recalculate the correct week
        const firstMealDate = new Date(mealDates[0]);
        const correctWeek = getWeekRange(firstMealDate);

        const newStartDate = correctWeek.startString;
        const newEndDate = correctWeek.endString;

        // Update the meal plan with correct date range
        const { error: updateError } = await supabase
          .from('meal_plans')
          .update({
            start_date: newStartDate,
            end_date: newEndDate,
            updated_at: new Date().toISOString()
          })
          .eq('id', plan.id);

        if (updateError) {
          console.error(`❌ [DatabaseService] Error updating plan ${plan.id}:`, updateError);
          return false;
        }

        fixedCount++;
      }

      if (fixedCount > 0) {
        console.log(`✅ [DatabaseService] Fixed ${fixedCount} meal plans`);
      }
      
      return true;

    } catch (error) {
      console.error('❌ [DatabaseService] Error in fixExistingMealPlanRanges:', error);
      return false;
    }
  }

  async getShoppingLists(): Promise<ShoppingList[]> {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        console.error('❌ [Database] No user ID found');
        return [];
      }

      const { data: shoppingLists, error } = await supabase
        .from('shopping_lists')
        .select(`
          *,
          shopping_list_items (*)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ [Database] Error fetching shopping lists:', error);
        return [];
      }

      return (shoppingLists || []).map((list: any) => ({
        id: list.id,
        title: list.title,
        notes: list.notes,
        isCompleted: list.is_completed,
        items: (list.shopping_list_items || []).map((item: any) => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          unit: item.unit,
          category: item.category,
          isPurchased: item.is_purchased,
          price: item.price,
          notes: item.notes,
          createdAt: item.created_at,
          updatedAt: item.updated_at
        })),
        createdAt: list.created_at,
        updatedAt: list.updated_at
      }));
    } catch (error) {
      console.error('❌ [Database] Error in getShoppingLists:', error);
      return [];
    }
  }

  async createShoppingList(shoppingList: {
    title: string;
    notes?: string;
    items: Array<{
      name: string;
      quantity: number;
      unit?: string;
      category?: string;
    }>;
  }): Promise<ShoppingList | null> {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        console.error('❌ [Database] No user ID found');
        return null;
      }

      // Create the shopping list
      const { data: newList, error: listError } = await supabase
        .from('shopping_lists')
        .insert({
          user_id: userId,
          title: shoppingList.title,
          notes: shoppingList.notes || null,
          is_completed: false
        })
        .select()
        .single();

      if (listError) {
        console.error('❌ [Database] Error creating shopping list:', listError);
        return null;
      }

      // Create the shopping list items
      if (shoppingList.items.length > 0) {
        const itemsToInsert = shoppingList.items.map(item => ({
          shopping_list_id: newList.id,
          name: item.name,
          quantity: item.quantity,
          unit: item.unit || null,
          category: item.category || null,
          is_purchased: false,
          price: null,
          notes: null
        }));

        const { data: newItems, error: itemsError } = await supabase
          .from('shopping_list_items')
          .insert(itemsToInsert)
          .select();

        if (itemsError) {
          console.error('❌ [Database] Error creating shopping list items:', itemsError);
          // Clean up the list if items failed to create
          await supabase.from('shopping_lists').delete().eq('id', newList.id);
          return null;
        }

        return {
          id: newList.id,
          title: newList.title,
          notes: newList.notes,
          isCompleted: newList.is_completed,
          items: (newItems || []).map((item: any) => ({
            id: item.id,
            name: item.name,
            quantity: item.quantity,
            unit: item.unit,
            category: item.category,
            isPurchased: item.is_purchased,
            price: item.price,
            notes: item.notes,
            createdAt: item.created_at,
            updatedAt: item.updated_at
          })),
          createdAt: newList.created_at,
          updatedAt: newList.updated_at
        };
      }

      return {
        id: newList.id,
        title: newList.title,
        notes: newList.notes,
        isCompleted: newList.is_completed,
        items: [],
        createdAt: newList.created_at,
        updatedAt: newList.updated_at
      };
    } catch (error) {
      console.error('❌ [Database] Error in createShoppingList:', error);
      return null;
    }
  }

  async updateShoppingListItem(itemId: string, updates: {
    isPurchased?: boolean;
    quantity?: number;
    notes?: string;
  }): Promise<boolean> {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        console.error('❌ [Database] No user ID found');
        return false;
      }

      // First verify the item belongs to the user by checking through the shopping_lists table
      const { data: itemData } = await supabase
        .from('shopping_list_items')
        .select(`
          id,
          shopping_lists!inner(user_id)
        `)
        .eq('id', itemId)
        .eq('shopping_lists.user_id', userId)
        .single();
      
      if (!itemData) {
        console.error('❌ [Database] Shopping list item not found or does not belong to user');
        return false;
      }

      const updateData: any = {};
      if (updates.isPurchased !== undefined) updateData.is_purchased = updates.isPurchased;
      if (updates.quantity !== undefined) updateData.quantity = updates.quantity;
      if (updates.notes !== undefined) updateData.notes = updates.notes;

      // Now update the item
      const { error } = await supabase
        .from('shopping_list_items')
        .update(updateData)
        .eq('id', itemId);

      if (error) {
        console.error('❌ [Database] Error updating shopping list item:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('❌ [Database] Error in updateShoppingListItem:', error);
      return false;
    }
  }

  async deleteShoppingListItem(itemId: string): Promise<boolean> {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        console.error('❌ [Database] No user ID found');
        return false;
      }

      // First verify the item belongs to the user by checking through the shopping_lists table
      const { data: itemData } = await supabase
        .from('shopping_list_items')
        .select(`
          id,
          shopping_lists!inner(user_id)
        `)
        .eq('id', itemId)
        .eq('shopping_lists.user_id', userId)
        .single();
      
      if (!itemData) {
        console.error('❌ [Database] Shopping list item not found or does not belong to user');
        return false;
      }

      // Now delete the item
      const { error } = await supabase
        .from('shopping_list_items')
        .delete()
        .eq('id', itemId);

      if (error) {
        console.error('❌ [Database] Error deleting shopping list item:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('❌ [Database] Error in deleteShoppingListItem:', error);
      return false;
    }
  }

  async deleteShoppingList(listId: string): Promise<boolean> {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        console.error('❌ [Database] No user ID found');
        return false;
      }

      const { error } = await supabase
        .from('shopping_lists')
        .delete()
        .eq('id', listId)
        .eq('user_id', userId);

      if (error) {
        console.error('❌ [Database] Error deleting shopping list:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('❌ [Database] Error in deleteShoppingList:', error);
      return false;
    }
  }

  async addShoppingListItem(item: {
    name: string;
    quantity: number;
    unit?: string;
    category?: string;
    listId?: string;
  }): Promise<ShoppingListItem | null> {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        console.error('❌ [Database] No user ID found');
        return null;
      }

      let targetListId = item.listId;
      
      // If no list specified, find or create "Manual Items" list
      if (!targetListId) {
        const manualListTitle = 'Manual Items';
        let { data: existingList } = await supabase
          .from('shopping_lists')
          .select('id')
          .eq('user_id', userId)
          .eq('title', manualListTitle)
          .single();
        
        if (!existingList) {
          // Create manual items list
          const { data: newList } = await supabase
            .from('shopping_lists')
            .insert({
              user_id: userId,
              title: manualListTitle,
              notes: 'Items added manually by user'
            })
            .select('id')
            .single();
          
          targetListId = newList?.id;
        } else {
          targetListId = existingList.id;
        }
      }
      
      if (!targetListId) throw new Error('Could not create or find shopping list');
      
      const { data, error } = await supabase
        .from('shopping_list_items')
        .insert({
          shopping_list_id: targetListId,
          name: item.name,
          quantity: item.quantity,
          unit: item.unit || null,
          category: item.category || 'Manual Items',
          is_purchased: false
        })
        .select('*')
        .single();
      
      if (error) {
        console.error('❌ [Database] Error adding shopping list item:', error);
        return null;
      }
      
      return transformShoppingListItem(data);
    } catch (error) {
      console.error('❌ [Database] Error in addShoppingListItem:', error);
      return null;
    }
  }

  async updateShoppingListItemName(itemId: string, newName: string): Promise<boolean> {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        console.error('❌ [Database] No user ID found');
        return false;
      }
      
      // First verify the item belongs to the user by checking through the shopping_lists table
      const { data: itemData } = await supabase
        .from('shopping_list_items')
        .select(`
          id,
          shopping_lists!inner(user_id)
        `)
        .eq('id', itemId)
        .eq('shopping_lists.user_id', userId)
        .single();
      
      if (!itemData) {
        console.error('❌ [Database] Shopping list item not found or does not belong to user');
        return false;
      }
      
      // Now update the item name
      const { error } = await supabase
        .from('shopping_list_items')
        .update({ name: newName })
        .eq('id', itemId);
      
      if (error) {
        console.error('❌ [Database] Error updating shopping list item name:', error);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('❌ [Database] Error in updateShoppingListItemName:', error);
      return false;
    }
  }

  // Ensure user has all required records using session data (fixes OAuth signup issue)
  async ensureUserPlanWithSession(userId: string, userEmail: string): Promise<{ success: boolean; message: string }> {
    try {
      console.log('🔍 [ensureUserPlanWithSession] Starting user record creation...');
      console.log('🔍 [ensureUserPlanWithSession] User ID:', userId);
      console.log('🔍 [ensureUserPlanWithSession] User email:', userEmail);

      // 1. Use the database function to ensure user_plans (gets limits from database)
      console.log('🔍 [ensureUserPlanWithSession] Calling ensure_user_plan RPC...');
      const { data: planData, error: planError } = await supabase.rpc('ensure_user_plan', {
        p_user_id: userId
      });

      if (planError) {
        console.error('❌ [ensureUserPlanWithSession] Error ensuring user plan:', planError);
        return { success: false, message: `Failed to create user plan: ${planError.message}` };
      }
      
      console.log('✅ [ensureUserPlanWithSession] User plan RPC result:', planData);

      // 2. Ensure profiles record exists
      console.log('🔍 [ensureUserPlanWithSession] Creating/updating profiles record...');
      
      // Check if profile already exists
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id, avatar_url')
        .eq('id', userId)
        .single();
      
      if (existingProfile) {
        console.log('✅ [ensureUserPlanWithSession] Profile already exists, skipping creation');
        // Profile already exists, don't overwrite it
      } else {
        // New user - generate avatar and create profile
        const defaultAvatar = await this.getRandomDefaultAvatar();
        console.log('🔍 [ensureUserPlanWithSession] New user, generating avatar:', defaultAvatar);
        
        const { error: profileError } = await supabase
          .from('profiles')
          .insert({
            id: userId,
            name: userEmail,
            email: userEmail,
            avatar_url: defaultAvatar,
            preferences: {},
            points: 0,
            level: 1,
            is_admin: false,
            plan_type: 'free'
          });

        if (profileError) {
          console.error('❌ [ensureUserPlanWithSession] Error creating profile:', profileError);
          return { success: false, message: `Failed to create profile: ${profileError.message}` };
        }
      }
      
      console.log('✅ [ensureUserPlanWithSession] Profile record created/updated successfully');

      // 3. Ensure ai_usage_tracking record exists for current month (only if missing)
      console.log('🔍 [ensureUserPlanWithSession] Checking usage tracking record...');
      const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM format
      
      // Check if usage tracking already exists
      const { data: existingUsage } = await supabase
        .from('ai_usage_tracking')
        .select('id')
        .eq('user_id', userId)
        .eq('usage_month', currentMonth)
        .single();
      
      if (!existingUsage) {
        // Only create if it doesn't exist - never overwrite existing usage
        console.log('🔍 [ensureUserPlanWithSession] Creating new usage tracking record...');
        const { error: usageError } = await supabase
          .from('ai_usage_tracking')
          .insert({
            user_id: userId,
            user_email: userEmail,
            usage_month: currentMonth,
            image_scans_used: 0,
            recipe_generations_used: 0,
            meal_analysis_used: 0,
            total_ai_requests_used: 0,
            next_reset_date: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toISOString()
          });
        
        if (usageError) {
          console.warn('⚠️ [ensureUserPlanWithSession] Error creating usage tracking (non-critical):', usageError);
        } else {
          console.log('✅ [ensureUserPlanWithSession] New usage tracking record created');
        }
      } else {
        console.log('✅ [ensureUserPlanWithSession] Usage tracking record already exists, preserving current values');
      }

      console.log('✅ [ensureUserPlanWithSession] All user records ensured (user_plans + profiles + usage_tracking)');
      return { success: true, message: 'All user records created successfully' };
    } catch (error) {
      console.error('💥 [ensureUserPlanWithSession] Exception ensuring user records:', error);
      return { success: false, message: String(error) };
    }
  }

  // Ensure user has all required records (fixes OAuth signup issue)
  async ensureUserPlan(): Promise<{ success: boolean; message: string }> {
    try {
      console.log('🔍 [ensureUserPlan] Starting user record creation...');
      
      const userId = await this.getCurrentUserId();
      if (!userId) {
        console.error('❌ [ensureUserPlan] No authenticated user ID found');
        return { success: false, message: 'No authenticated user' };
      }
      
      console.log('🔍 [ensureUserPlan] User ID:', userId);

      // Get user info from auth.users
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        console.error('❌ [ensureUserPlan] Failed to get user info:', userError);
        return { success: false, message: 'Failed to get user info' };
      }
      
      console.log('🔍 [ensureUserPlan] User email:', user.email);

      // Use the session-based version
      return await this.ensureUserPlanWithSession(userId, user.email || '');
    } catch (error) {
      console.error('💥 [ensureUserPlan] Exception ensuring user records:', error);
      return { success: false, message: String(error) };
    }
  }
}

// Export singleton instance for use throughout the app
export const databaseService = DatabaseService.getInstance();
export const db = databaseService; // Backward compatibility alias

// Daily Nutrition Tracking types (add these at the top with other interfaces)
export interface DatabaseDailyNutritionTracking {
  id: string;
  user_id: string;
  tracking_date: string;
  total_calories: number;
  total_protein: number;
  total_carbs: number;
  total_fat: number;
  total_fiber: number;
  total_sugar: number;
  total_sodium: number;
  meals_count: number;
  recipes_count: number;
  created_at: string;
  updated_at: string;
}

export interface DatabaseDailyNutritionEntry {
  id: string;
  user_id: string;
  tracking_date: string;
  entry_type: 'meal' | 'recipe';
  entry_id: string;
  entry_name: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
  sugar: number;
  sodium: number;
  servings: number;
  serving_size: string | null;
  created_at: string;
  updated_at: string;
}

export interface DailyNutritionSummary {
  totalCalories: number;
  totalProtein: number;
  totalCarbs: number;
  totalFat: number;
  totalFiber: number;
  totalSugar: number;
  totalSodium: number;
  mealsCount: number;
  recipesCount: number;
  entriesCount: number;
}

export interface NutritionEntry {
  id: string;
  trackingDate: string;
  entryType: 'meal' | 'recipe';
  entryId: string;
  entryName: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
  sugar: number;
  sodium: number;
  servings: number;
  servingSize: string;
  createdAt: string;
  updatedAt: string;
}

// Meal Planning Types
export interface MealPlan {
  id: string;
  startDate: string;
  endDate: string;
  meals: Record<string, {
    breakfast?: {
      recipeId: string;
      recipeName: string;
      servings: number;
      calories: number;
      cookTime: number;
    };
    lunch?: {
      recipeId: string;
      recipeName: string;
      servings: number;
      calories: number;
      cookTime: number;
    };
    dinner?: {
      recipeId: string;
      recipeName: string;
      servings: number;
      calories: number;
      cookTime: number;
    };
  }>;
}

// Export singleton instance
